import { useState, type ReactNode } from 'react';
import type { LedgerData } from './AssetLedger.js';

/**
 * 资产台账的新增 / 修改面板（C-181 A5，原型 `addPanelHTML`）。
 *
 * 「＋新增」在工具条上，面板展开在表格上方；四张台账各一种表单。
 * 修改复用同一个面板——字段预填，不能改的禁用并写明为什么。
 *
 * ## 边界（与台账的职责一致）
 *
 * - 四段与 TEST **不在**这些表单里——走评审流程（卡页），不在台账改
 * - 基线五项**不在**——在 ATC 详情页
 * - 在岗人数**没有**录入框——那是 `person_assignment` 数出来的统计值；
 *   编制人数才是人填的那个（「规划几个人」），两个数不同源不同义
 * - 人员没有「归档」「恢复」——只有在职 / 离职
 */

/** 表单交给取数壳的动作。每个返回一句回执（A6），抛错即失败原因。 */
export type LedgerActions = {
  saveDepartment: (input: DepartmentFormValue) => Promise<string>;
  savePosition: (input: PositionFormValue) => Promise<string>;
  saveAtc: (input: AtcFormValue) => Promise<string>;
  setStatus: (
    kind: 'department' | 'position' | 'atc',
    id: string,
    next: string,
    reason: string,
  ) => Promise<string>;
  savePerson: (input: PersonFormValue) => Promise<string>;
  reassignPerson: (employeeNo: string, positionId: string, validFrom: string) => Promise<string>;
  departPerson: (employeeNo: string, employedTo: string) => Promise<string>;
};

export type DepartmentFormValue = {
  id?: string;
  name: string;
  cls: 'operational' | 'construction';
  headRef: string;
  charter: string;
  headcountSurveyed: number | null;
  sourceNote: string;
};

export type PositionFormValue = {
  id?: string;
  departmentId?: string;
  name: string;
  code: string;
  mission: string;
  responsibility: string;
  /** 自由文本，取数壳包成对象（`{ 内容: text }`）再发。 */
  authorityScopeText: string;
  competencyText: string;
  plannedCardCount: number | null;
  /** 编制人数（「规划几个人」）。新增时与卡位一次填完；修改不在这里改。 */
  headcount?: number | null;
};

export type AtcFormValue = {
  id?: string;
  positionId?: string;
  name: string;
};

export type PersonFormValue = {
  employeeNo?: string;
  name: string;
  externalRef: string;
  employedFrom?: string;
  positionId?: string;
};

/** 面板当前在编辑谁。`id` 为空即新增；人员另有两个独立动作（调岗 / 离职）。 */
export type PanelTarget =
  | { kind: 'dep' | 'pos' | 'atc'; id?: string }
  | { kind: 'person'; id: string; mode: 'edit' | 'move' | 'leave' }
  | { kind: 'person'; id?: undefined; mode?: 'create' };

function numOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** 面板。字段值在本地 state；保存交给 actions，成功后外层关面板、亮回执。 */
export function LedgerFormPanel({
  target,
  data,
  actions,
  onDone,
  onCancel,
}: {
  target: PanelTarget;
  /** 全量四张表——下拉要用没被收窄的那份。 */
  data: LedgerData;
  actions: LedgerActions;
  onDone: (message: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<string>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onDone(await action());
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  const title =
    target.kind === 'dep'
      ? target.id === undefined
        ? '新增部门'
        : '修改部门'
      : target.kind === 'pos'
        ? target.id === undefined
          ? '新增岗位'
          : '修改岗位'
        : target.kind === 'atc'
          ? target.id === undefined
            ? '新增 ATC'
            : '修改 ATC'
          : target.kind === 'person'
            ? target.mode === 'move'
              ? '调岗'
              : target.mode === 'leave'
                ? '离职登记'
                : target.mode === 'edit'
                  ? '修改人员'
                  : '新增人员'
            : '';

  // 拆开成显式分支：联合类型在 JSX 的 && 里收窄不稳，if 分支干干净净
  let form: ReactNode;
  if (target.kind === 'dep') {
    form = <DepForm target={target} data={data} busy={busy} error={error} run={run} actions={actions} onCancel={onCancel} />;
  } else if (target.kind === 'pos') {
    form = <PosForm target={target} data={data} busy={busy} error={error} run={run} actions={actions} onCancel={onCancel} />;
  } else if (target.kind === 'atc') {
    form = <AtcForm target={target} data={data} busy={busy} error={error} run={run} actions={actions} onCancel={onCancel} />;
  } else {
    form = <PersonForm target={target} data={data} busy={busy} error={error} run={run} actions={actions} onCancel={onCancel} />;
  }

  return (
    <div className="addpanel">
      <div className="t3">{title}</div>
      {form}
    </div>
  );
}

type SharedProps = {
  busy: boolean;
  error: string | null;
  run: (action: () => Promise<string>) => Promise<void>;
  actions: LedgerActions;
  onCancel: () => void;
};

function Buttons({ busy, onCancel, label }: { busy: boolean; onCancel: () => void; label: string }): ReactNode {
  return (
    <>
      <button className="btnp" type="submit" disabled={busy}>
        {busy ? '保存中…' : label}
      </button>
      <button type="button" className="linkbtn" onClick={onCancel} disabled={busy}>
        取消
      </button>
    </>
  );
}

/* ═══════ 部门 ═══════ */

function DepForm({
  target,
  data,
  busy,
  error,
  run,
  actions,
  onCancel,
}: SharedProps & { target: { kind: string; id?: string }; data: LedgerData }): ReactNode {
  const current = data.departments.find((d) => d.id === target.id);
  const [name, setName] = useState(current?.name ?? '');
  const [cls, setCls] = useState<'operational' | 'construction'>(current?.cls ?? 'operational');
  const [headRef, setHeadRef] = useState(current?.headRef ?? '');
  const [charter, setCharter] = useState(current?.charter ?? '');
  const [surveyed, setSurveyed] = useState(current?.headcountSurveyed?.toString() ?? '');
  const [sourceNote, setSourceNote] = useState(current?.sourceNote ?? '');
  const editing = current !== undefined;

  return (
    <form
      className="frm"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() === '') return;
        void run(() =>
          actions.saveDepartment({
            id: current?.id,
            name: name.trim(),
            cls,
            headRef: headRef.trim(),
            charter: charter.trim(),
            headcountSurveyed: numOrNull(surveyed),
            sourceNote: sourceNote.trim(),
          }),
        );
      }}
    >
      <label>
        部门名称
        <input id="lf-dep-name" placeholder="例如：质量管理部" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {/*
        编码没有输入框（C-198 / ADR-0052）：两位数字编码由系统按号段发放、
        创建即锁——表单收它就成了一条写入旁路。编辑态只读展示。
      */}
      {editing && (
        <label>
          编码（系统发放 · 创建即锁）
          <input id="lf-dep-code" value={current.code} disabled />
        </label>
      )}
      <label>
        类别
        <select id="lf-dep-cls" value={cls} onChange={(e) => setCls(e.target.value as 'operational' | 'construction')}>
          <option value="operational">运营类</option>
          <option value="construction">建设类（交付）</option>
        </select>
      </label>
      <label>
        部门负责人
        <input id="lf-dep-head" value={headRef} onChange={(e) => setHeadRef(e.target.value)} />
      </label>
      <label>
        主要职能简述
        <textarea id="lf-dep-charter" rows={2} value={charter} onChange={(e) => setCharter(e.target.value)} />
      </label>
      <label>
        问卷填报人数
        <input id="lf-dep-surveyed" inputMode="numeric" value={surveyed} onChange={(e) => setSurveyed(e.target.value)} />
      </label>
      <label>
        来源标注
        <input id="lf-dep-source" value={sourceNote} onChange={(e) => setSourceNote(e.target.value)} />
      </label>
      {error !== null && <p className="note">{error}</p>}
      <Buttons busy={busy} onCancel={onCancel} label={editing ? '保存修改' : '保存部门'} />
    </form>
  );
}

/* ═══════ 岗位 ═══════ */

function PosForm({
  target,
  data,
  busy,
  error,
  run,
  actions,
  onCancel,
}: SharedProps & { target: { kind: string; id?: string }; data: LedgerData }): ReactNode {
  const current = data.positions.find((p) => p.id === target.id);
  const editing = current !== undefined;
  const [departmentId, setDepartmentId] = useState(current?.departmentId ?? data.departments[0]?.id ?? '');
  const [name, setName] = useState(current?.name ?? '');
  const [code, setCode] = useState(current?.code ?? '');
  const [mission, setMission] = useState(current?.mission ?? '');
  const [responsibility, setResponsibility] = useState(current?.responsibility ?? '');
  const [authorityText, setAuthorityText] = useState('');
  const [competencyText, setCompetencyText] = useState('');
  const [planned, setPlanned] = useState(current?.plannedCardCount?.toString() ?? '');
  const [headcount, setHeadcount] = useState('');

  return (
    <form
      className="frm"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() === '') return;
        void run(() =>
          actions.savePosition({
            id: current?.id,
            departmentId: editing ? undefined : departmentId,
            name: name.trim(),
            code: code.trim(),
            mission: mission.trim(),
            responsibility: responsibility.trim(),
            authorityScopeText: authorityText.trim(),
            competencyText: competencyText.trim(),
            plannedCardCount: numOrNull(planned),
            headcount: editing ? undefined : numOrNull(headcount),
          }),
        );
      }}
    >
      <label>
        所属部门{editing ? '（不可改）' : ''}
        <select id="lf-pos-dept" value={departmentId} disabled={editing} onChange={(e) => setDepartmentId(e.target.value)}>
          {data.departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </label>
      {editing && (
        <p className="note">岗位改属部门属组织调整，走停用旧岗＋新建新岗表达，不做移动（历史以区间保留）。</p>
      )}
      <label>
        岗位名称
        <input id="lf-pos-name" placeholder="例如：质检计划岗" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        编码{editing ? '（不可改）' : '（留空由系统生成）'}
        <input id="lf-pos-code" value={code} disabled={editing} onChange={(e) => setCode(e.target.value)} />
      </label>
      <label>
        岗位使命
        <textarea id="lf-pos-mission" rows={2} value={mission} onChange={(e) => setMission(e.target.value)} />
      </label>
      <label>
        责任
        <textarea id="lf-pos-resp" rows={2} value={responsibility} onChange={(e) => setResponsibility(e.target.value)} />
      </label>
      <label>
        权限边界
        <textarea id="lf-pos-auth" rows={2} value={authorityText} onChange={(e) => setAuthorityText(e.target.value)} />
      </label>
      <label>
        胜任标准
        <textarea id="lf-pos-comp" rows={2} value={competencyText} onChange={(e) => setCompetencyText(e.target.value)} />
      </label>
      <label>
        卡位总数
        <input id="lf-pos-planned" inputMode="numeric" value={planned} onChange={(e) => setPlanned(e.target.value)} />
      </label>
      {!editing && (
        <label>
          编制人数
          <input id="lf-pos-headcount" inputMode="numeric" value={headcount} onChange={(e) => setHeadcount(e.target.value)} />
        </label>
      )}
      <p className="note">编制是「规划几个人」（人填的调研结论）；在岗人数由系统按在岗关系统计，没有录入入口。</p>
      {error !== null && <p className="note">{error}</p>}
      <Buttons busy={busy} onCancel={onCancel} label={editing ? '保存修改' : '保存岗位'} />
    </form>
  );
}

/* ═══════ ATC ═══════ */

function AtcForm({
  target,
  data,
  busy,
  error,
  run,
  actions,
  onCancel,
}: SharedProps & { target: { kind: string; id?: string }; data: LedgerData }): ReactNode {
  const current = data.atcs.find((a) => a.id === target.id);
  const editing = current !== undefined;
  const [departmentId, setDepartmentId] = useState(() => {
    if (current !== undefined) {
      return data.positions.find((p) => p.id === current.positionId)?.departmentId ?? '';
    }
    return data.departments[0]?.id ?? '';
  });
  const positionsOfDept = data.positions.filter((p) => p.departmentId === departmentId);
  const [positionId, setPositionId] = useState(current?.positionId ?? '');
  const [name, setName] = useState(current?.name ?? '');
  const effectivePositionId = editing
    ? (current?.positionId ?? '')
    : positionsOfDept.some((p) => p.id === positionId)
      ? positionId
      : (positionsOfDept[0]?.id ?? '');

  return (
    <form
      className="frm"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() === '') return;
        void run(() =>
          actions.saveAtc({
            id: current?.id,
            positionId: editing ? undefined : effectivePositionId,
            name: name.trim(),
          }),
        );
      }}
    >
      {editing ? (
        <label>
          编号（不可改）
          <input value={current.code} disabled />
        </label>
      ) : (
        <>
          <label>
            所属部门
            <select
              id="lf-atc-dept"
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setPositionId('');
              }}
            >
              {data.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            责任岗位
            <select
              id="lf-atc-pos"
              value={effectivePositionId}
              onChange={(e) => setPositionId(e.target.value)}
            >
              {positionsOfDept.length === 0 && <option value="">（该部门暂无岗位）</option>}
              {positionsOfDept.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      <label>
        ATC 名称（岗位＋动词＋动作）
        <input
          id="lf-atc-name"
          placeholder="例如：质检员核对进厂检验记录"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {editing ? (
        <p className="note">编号与归属不可改；四段与 TEST 的修订在评审流程中进行，不在台账改。</p>
      ) : (
        <>
          <p className="note">
            编号将在保存时自动生成；类别随所属部门（
            {data.departments.find((d) => d.id === departmentId)?.cls === 'construction' ? '建设类' : '运营类'}）。
          </p>
          <p className="note">保存后：状态「待补全」 · 类别随所属部门 · 四段与 TEST 待补全。</p>
        </>
      )}
      {error !== null && <p className="note">{error}</p>}
      <Buttons busy={busy} onCancel={onCancel} label={editing ? '保存修改' : '保存 ATC'} />
    </form>
  );
}

/* ═══════ 人员 ═══════ */

function PersonForm({
  target,
  data,
  busy,
  error,
  run,
  actions,
  onCancel,
}: SharedProps & { target: PanelTarget; data: LedgerData }): ReactNode {
  const current = data.people.find((r) => r.employeeNo === target.id);
  const mode = 'mode' in target ? (target.mode ?? 'create') : 'create';

  const [name, setName] = useState(current?.name ?? '');
  const [externalRef, setExternalRef] = useState(current?.externalRef ?? '');
  const [departmentId, setDepartmentId] = useState(
    current?.departmentId ?? data.departments[0]?.id ?? '',
  );
  const positionsOfDept = data.positions.filter((p) => p.departmentId === departmentId);
  const [positionId, setPositionId] = useState(current?.positionId ?? '');
  const [date, setDate] = useState('');
  const effectivePositionId = positionsOfDept.some((p) => p.id === positionId)
    ? positionId
    : (positionsOfDept[0]?.id ?? '');

  return (
    <form
      className="frm"
      onSubmit={(e) => {
        e.preventDefault();
        const targetNo = current?.employeeNo;
        if (mode === 'move') {
          if (targetNo === undefined || date === '') return;
          void run(() => actions.reassignPerson(targetNo, effectivePositionId, date));
          return;
        }
        if (mode === 'leave') {
          if (targetNo === undefined || date === '') return;
          void run(() => actions.departPerson(targetNo, date));
          return;
        }
        if (name.trim() === '') return;
        void run(() =>
          actions.savePerson({
            employeeNo: mode === 'edit' ? target.id : undefined,
            name: name.trim(),
            externalRef: externalRef.trim(),
            employedFrom: mode === 'create' ? date || undefined : undefined,
            positionId: mode === 'create' ? effectivePositionId : undefined,
          }),
        );
      }}
    >
      {current !== undefined && (
        <label>
          工号（锚点，不可改）
          <input value={current.employeeNo} disabled />
        </label>
      )}
      {mode === 'create' && <p className="note">工号将在保存时自动生成，生成后不可改。</p>}

      {(mode === 'create' || mode === 'edit') && (
        <>
          <label>
            姓名
            <input id="lf-person-name" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            客户侧标识（不是锚点）
            <input id="lf-person-ref" value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
          </label>
        </>
      )}

      {mode === 'edit' && current !== undefined && (
        <p className="note">
          所属岗位只读：{current.positionName ?? '（未在岗）'}。调岗是独立动作（闭合旧区间＋追加新区间）。
        </p>
      )}

      {(mode === 'create' || mode === 'move') && (
        <>
          <label>
            {mode === 'create' ? '所属部门' : '新部门'}
            <select
              id="lf-person-dept"
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setPositionId('');
              }}
            >
              {data.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {mode === 'create' ? '所属岗位' : '新岗位'}
            <select id="lf-person-pos" value={effectivePositionId} onChange={(e) => setPositionId(e.target.value)}>
              {positionsOfDept.length === 0 && <option value="">（该部门暂无岗位）</option>}
              {positionsOfDept.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {mode === 'create' && (
        <label>
          在岗起始
          <input id="lf-person-from" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      )}
      {mode === 'move' && (
        <label>
          生效日期
          <input id="lf-person-move" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      )}
      {mode === 'leave' && (
        <>
          <label>
            离职日期
            <input id="lf-person-leave" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <p className="note">离职一次性写入，写下不可再改；在岗区间同时闭合。</p>
        </>
      )}

      {error !== null && <p className="note">{error}</p>}
      <Buttons
        busy={busy}
        onCancel={onCancel}
        label={mode === 'move' ? '保存调岗' : mode === 'leave' ? '登记离职' : mode === 'edit' ? '保存修改' : '保存人员'}
      />
    </form>
  );
}
